import { useEffect } from 'react';
import { PREVIEWFRAME } from '../../config';
import { IResponse } from '../../types';
import { useSetAtom, useAtom } from 'jotai';
import { notificationAtom, pageLoadingAtom } from '../../atoms/system';
import { ShapeType, QuestionShowType } from '../../types';
import api from '../../api';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { Popover } from '@headlessui/react';
import { HexAlphaColorPicker, HexColorInput } from 'react-colorful';
import queryString from 'query-string';
import { Theme, ThemeSetting } from '../../schema';

const ThemeLayout = (): JSX.Element => {
  const notification = useSetAtom(notificationAtom);
  const [loading, pageLoading] = useAtom(pageLoadingAtom);
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ThemeSetting>({
    resolver: zodResolver(Theme),
  });
  useEffect(() => {
    (async () => {
      try {
        pageLoading(true);
        const result = await api
          .get('api/settings/theme')
          .json<IResponse<ThemeSetting>>();
        reset(result.data);
      } catch (e) {
        console.log(e);
        notification({
          show: true,
          type: null,
          title: null,
        });
      } finally {
        pageLoading(false);
      }
    })();
  }, []);

  const onSubmit = async (data: ThemeSetting) => {
    try {
      pageLoading(true);
      await api.post('api/settings/theme', {
        json: data,
      });
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: null,
      });
    } finally {
      pageLoading(false);
    }
  };

  const frameWatch = watch([
    'reviewTitle',
    'questionTitle',
    'shapeType',
    'colorShape',
    'colorBgShape',
    'colorMain',
    'showTitle',
    'showDate',
    'showSort',
    'acceptImage',
    'acceptVideo',
    'acceptQuestion',
    'questionType',
    'acceptReply',
    'acceptVerifiedBadge',
    'alwaysVerifiedBadge',
    'colorVerifiedBadge',
  ]);

  const frameQuery = {
    reviewTitle: frameWatch[0],
    questionTitle: frameWatch[1],
    shapeType: frameWatch[2],
    colorShape: frameWatch[3],
    colorBgShape: frameWatch[4],
    colorMain: frameWatch[5],
    showTitle: frameWatch[6],
    showDate: frameWatch[7],
    showSort: frameWatch[8],
    acceptImage: frameWatch[9],
    acceptVideo: frameWatch[10],
    acceptQuestion: frameWatch[11],
    questionType: frameWatch[12],
    acceptReply: frameWatch[13],
    acceptVerifiedBadge: frameWatch[14],
    alwaysVerifiedBadge: frameWatch[15],
    colorVerifiedBadge: frameWatch[16],
  };

  return (
    <div className="flex flex-nowrap px-4 py-8 space-x-4 min-w-[1400px]">
      <div className="flex flex-1  min-w-[200px]">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col space-y-8 w-full"
        >
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Tiêu đề đánh giá
            </label>
            <input
              type="text"
              {...register('reviewTitle')}
              className={clsx(
                errors.reviewTitle?.message
                  ? 'border-red-300 text-red-600 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500',
                'appearance-none block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-xs',
              )}
            />
            {errors.reviewTitle?.message ? (
              <p className="text-xs text-red-600">
                {errors.reviewTitle.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Tiêu đề câu hỏi
            </label>
            <input
              type="text"
              {...register('questionTitle')}
              className={clsx(
                errors.questionTitle?.message
                  ? 'border-red-300 text-red-600 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500',
                'appearance-none block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-xs',
              )}
            />
            {errors.questionTitle?.message ? (
              <p className="text-xs text-red-600">
                {errors.questionTitle.message}
              </p>
            ) : null}
          </div>
          <div className="space-x-6 flex">
            <label className="block text-xs font-medium text-gray-700">
              Kiểu icon
            </label>
            <div className="flex flex-col space-y-3">
              <div className="flex flex-1 items-center">
                <input
                  id="shapeType-star_1"
                  type="radio"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-full"
                  {...register('shapeType')}
                  value={ShapeType.star_1}
                />
                <label
                  htmlFor="shapeType-star_1"
                  className="ml-2 block text-xs font-medium text-gray-700"
                >
                  Ngôi sao 1
                </label>
              </div>
              <div className="flex flex-1 items-center">
                <input
                  id="shapeType-star_2"
                  type="radio"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-full"
                  {...register('shapeType')}
                  value={ShapeType.star_2}
                />
                <label
                  htmlFor="shapeType-star_2"
                  className="ml-2 block text-xs font-medium text-gray-700"
                >
                  Ngôi sao 2
                </label>
              </div>
              <div className="flex flex-1 items-center">
                <input
                  id="shapeType-heart_1"
                  type="radio"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-full"
                  {...register('shapeType')}
                  value={ShapeType.heart_1}
                />
                <label
                  htmlFor="shapeType-heart_1"
                  className="ml-2 block text-xs font-medium text-gray-700"
                >
                  Trái tim 1
                </label>
              </div>
              <div className="flex flex-1 items-center">
                <input
                  id="shapeType-heart_2"
                  type="radio"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-full"
                  {...register('shapeType')}
                  value={ShapeType.heart_2}
                />
                <label
                  htmlFor="shapeType-heart_2"
                  className="ml-2 block text-xs font-medium text-gray-700"
                >
                  Trái tim 2
                </label>
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Màu icon
            </label>
            <div className="relative rounded-md shadow-sm">
              <HexColorInput
                color={watch('colorShape')}
                onChange={color => setValue('colorShape', color)}
                prefixed
                alpha
                className={clsx(
                  errors.colorShape?.message
                    ? 'border-red-300 text-red-600 placeholder-red-300 focus:ring-red-500 focus:border-red-500 focus:outline-red-500'
                    : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-indigo-500',
                  'appearance-none block w-full px-3 py-2 border rounded-md sm:text-xs',
                )}
              />
              <div className="absolute inset-y-[3px] right-[3px] flex items-center">
                <Popover className="relative h-full">
                  <Popover.Button
                    className="h-full w-10 border border-gray-200 rounded-md outline-none"
                    style={{
                      backgroundColor: watch('colorShape'),
                    }}
                  >
                    &nbsp;
                  </Popover.Button>
                  <Popover.Panel className="absolute z-10 shadow-md rounded-md">
                    <HexAlphaColorPicker
                      color={getValues('colorShape')}
                      onChange={color => setValue('colorShape', color)}
                    />
                  </Popover.Panel>
                </Popover>
              </div>
            </div>
            {errors.colorShape?.message ? (
              <p className="text-xs text-red-600">
                {errors.colorShape?.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Màu nền icon
            </label>
            <div className="relative rounded-md shadow-sm">
              <HexColorInput
                color={watch('colorBgShape')}
                onChange={color => setValue('colorBgShape', color)}
                prefixed
                alpha
                className={clsx(
                  errors.colorBgShape?.message
                    ? 'border-red-300 text-red-600 placeholder-red-300 focus:ring-red-500 focus:border-red-500 focus:outline-red-500'
                    : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-indigo-500',
                  'appearance-none block w-full px-3 py-2 border rounded-md sm:text-xs',
                )}
              />
              <div className="absolute inset-y-[3px] right-[3px] flex items-center">
                <Popover className="relative h-full">
                  <Popover.Button
                    className="h-full w-10 border border-gray-200 rounded-md outline-none"
                    style={{
                      backgroundColor: watch('colorBgShape'),
                    }}
                  >
                    &nbsp;
                  </Popover.Button>
                  <Popover.Panel className="absolute z-10 shadow-md rounded-md">
                    <HexAlphaColorPicker
                      color={getValues('colorBgShape')}
                      onChange={color => setValue('colorBgShape', color)}
                    />
                  </Popover.Panel>
                </Popover>
              </div>
            </div>
            {errors.colorBgShape?.message ? (
              <p className="text-xs text-red-600">
                {errors.colorBgShape?.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Màu chủ đạo
            </label>
            <div className="relative rounded-md shadow-sm">
              <HexColorInput
                color={watch('colorMain')}
                onChange={color => setValue('colorMain', color)}
                prefixed
                alpha
                className={clsx(
                  errors.colorMain?.message
                    ? 'border-red-300 text-red-600 placeholder-red-300 focus:ring-red-500 focus:border-red-500 focus:outline-red-500'
                    : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-indigo-500',
                  'appearance-none block w-full px-3 py-2 border rounded-md sm:text-xs',
                )}
              />
              <div className="absolute inset-y-[3px] right-[3px] flex items-center">
                <Popover className="relative h-full">
                  <Popover.Button
                    className="h-full w-10 border border-gray-200 rounded-md outline-none"
                    style={{
                      backgroundColor: watch('colorMain'),
                    }}
                  >
                    &nbsp;
                  </Popover.Button>
                  <Popover.Panel className="absolute z-10 shadow-md rounded-md">
                    <HexAlphaColorPicker
                      color={getValues('colorMain')}
                      onChange={color => setValue('colorMain', color)}
                    />
                  </Popover.Panel>
                </Popover>
              </div>
            </div>
            {errors.colorMain?.message ? (
              <p className="text-xs text-red-600">
                {errors.colorMain?.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <div className="flex items-center">
              <input
                id="approveAuto"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                {...register('approveAuto')}
              />
              <label
                htmlFor="approveAuto"
                className="ml-2 block text-xs font-medium text-gray-700"
              >
                Tự động duyệt đánh giá
              </label>
            </div>
            <p className="text-xs text-gray-400 ">
              Tắt nếu bạn muốn tự duyệt đánh giá trước khi hiển thị đánh giá
            </p>
          </div>
          <div className="flex items-center">
            <input
              id="showTitle"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              {...register('showTitle')}
            />
            <label
              htmlFor="showTitle"
              className="ml-2 block text-xs font-medium text-gray-700"
            >
              Hiển thị tiêu đề đánh giá
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="showDate"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              {...register('showDate')}
            />
            <label
              htmlFor="showDate"
              className="ml-2 block text-xs font-medium text-gray-700"
            >
              Hiển thị thời gian đánh giá
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="showSort"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              {...register('showSort')}
            />
            <label
              htmlFor="showSort"
              className="ml-2 block text-xs font-medium text-gray-700"
            >
              Hiển thị sắp xếp đánh giá
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="acceptImage"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              {...register('acceptImage')}
            />
            <label
              htmlFor="acceptImage"
              className="ml-2 block text-xs font-medium text-gray-700"
            >
              Cho phép hình ảnh đánh giá
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="acceptVideo"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              {...register('acceptVideo')}
            />
            <label
              htmlFor="acceptVideo"
              className="ml-2 block text-xs font-medium text-gray-700"
            >
              Cho phép video đánh giá
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="acceptQuestion"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              {...register('acceptQuestion')}
            />
            <label
              htmlFor="acceptQuestion"
              className="ml-2 block text-xs font-medium text-gray-700"
            >
              Cho phép câu hỏi thường gặp
            </label>
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Kiểu hiển thị câu hỏi thường gặp
            </label>
            <div className="flex">
              <div className="flex flex-1 items-center">
                <input
                  id="questionType-list"
                  type="radio"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-full"
                  {...register('questionType')}
                  value={QuestionShowType.list}
                />
                <label
                  htmlFor="questionType-list"
                  className="ml-2 block text-xs font-medium text-gray-700"
                >
                  Dạng list
                </label>
              </div>
              <div className="flex flex-1 items-center">
                <input
                  id="questionType-tab"
                  type="radio"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-full"
                  {...register('questionType')}
                  value={QuestionShowType.tab}
                />
                <label
                  htmlFor="questionType-tab"
                  className="ml-2 block text-xs font-medium text-gray-700"
                >
                  Dạng tab
                </label>
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <input
              id="acceptReply"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              {...register('acceptReply')}
            />
            <label
              htmlFor="acceptReply"
              className="ml-2 block text-xs font-medium text-gray-700"
            >
              Cho phép chức năng trả lời
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="acceptVerifiedBadge"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              {...register('acceptVerifiedBadge')}
            />
            <label
              htmlFor="acceptVerifiedBadge"
              className="ml-2 block text-xs font-medium text-gray-700"
            >
              Hiển thị chứng thực mua hàng
            </label>
          </div>
          <div className="space-y-1">
            <div className="flex items-center">
              <input
                id="alwaysVerifiedBadge"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                {...register('alwaysVerifiedBadge')}
              />
              <label
                htmlFor="alwaysVerifiedBadge"
                className="ml-2 block text-xs font-medium text-gray-700"
              >
                Hiển thị chứng thực với mọi đánh giá
              </label>
            </div>
            <p className="text-xs text-gray-400 ">
              Tắt nếu bạn muốn chứng thực chỉ hiển thị với những người đã thực
              sự mua hàng
            </p>
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700">
              Màu nền huy hiệu chứng thực
            </label>
            <div className="relative rounded-md shadow-sm">
              <HexColorInput
                color={watch('colorVerifiedBadge')}
                onChange={color => setValue('colorVerifiedBadge', color)}
                prefixed
                alpha
                className={clsx(
                  errors.colorVerifiedBadge?.message
                    ? 'border-red-300 text-red-600 placeholder-red-300 focus:ring-red-500 focus:border-red-500 focus:outline-red-500'
                    : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-indigo-500',
                  'appearance-none block w-full px-3 py-2 border rounded-md sm:text-xs',
                )}
              />
              <div className="absolute inset-y-[3px] right-[3px] flex items-center">
                <Popover className="relative h-full">
                  <Popover.Button
                    className="h-full w-10 border border-gray-200 rounded-md outline-none"
                    style={{
                      backgroundColor: watch('colorVerifiedBadge'),
                    }}
                  >
                    &nbsp;
                  </Popover.Button>
                  <Popover.Panel className="absolute z-10 shadow-md rounded-md">
                    <HexAlphaColorPicker
                      color={getValues('colorVerifiedBadge')}
                      onChange={color => setValue('colorVerifiedBadge', color)}
                    />
                  </Popover.Panel>
                </Popover>
              </div>
            </div>
            {errors.colorVerifiedBadge?.message ? (
              <p className="text-xs text-red-600">
                {errors.colorVerifiedBadge?.message}
              </p>
            ) : null}
          </div>
          <div className="flex items-center">
            <input
              id="requireLogin"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              {...register('requireLogin')}
            />
            <label
              htmlFor="requireLogin"
              className="ml-2 block text-xs font-medium text-gray-700"
            >
              Bắt buộc đăng nhập để đánh giá
            </label>
          </div>
          <button
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cập nhật
          </button>
        </form>
      </div>
      <div className="relative flex flex-col space-y-2 min-w-[1190px] min-h-[1700px] overflow-hidden overflow-y-auto">
        <div
          className={clsx(
            loading ? 'bg-white' : 'bg-transparent',
            'absolute top-0 left-0 z-1 w-full h-full',
          )}
        ></div>
        <p className="text-md text-center font-medium">Xem Trước</p>
        <iframe
          className="outline-none shadow-2xl border border-gray-200 h-full w-full rounded-lg"
          src={`${PREVIEWFRAME}/#/main?${queryString.stringify(frameQuery)}`}
          title="theme"
        ></iframe>
      </div>
    </div>
  );
};

export default ThemeLayout;
